import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';

const API_BASE = 'https://ctqysft3ek.execute-api.eu-west-2.amazonaws.com/tasks';

async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

async function apiRequest(method, path, body) {
  const idToken = await getIdToken();
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = idToken;

  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);

  try {
    const res = await fetch(`${API_BASE}${path}`, opts);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) {
        toast.error('Session expired. Please sign in again.');
        // Clear stale auth state — next checkAuth() will see unauthenticated
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error('Session expired');
      }
      const message = data.error || data.message || `API error: ${method} ${path}`;
      toast.error(message);
      throw new Error(message);
    }
    return data;
  } catch (err) {
    if (!err.message?.startsWith('API error')) {
      console.error(`API ${method} ${path} failed:`, err);
      toast.error(err.message || `API error: ${method} ${path}`);
    }
    throw err;
  }
}

// Tasks
export async function fetchAllTasks() {
  return apiRequest('GET', '/tasks');
}

export async function createTask(task) {
  return apiRequest('POST', '/tasks', task);
}

export async function updateTask(task) {
  return apiRequest('PUT', `/tasks/${task.id || task.taskId}`, task);
}

export async function completeTask(taskId) {
  return apiRequest('PATCH', `/tasks/${taskId}/complete`);
}

export async function uncompleteTask(taskId) {
  return apiRequest('PATCH', `/tasks/${taskId}/uncomplete`);
}

export async function moveTask(taskId, status, projectId) {
  return apiRequest('PATCH', `/tasks/${taskId}/move`, { status, projectId });
}

export async function deleteTask(taskId) {
  return apiRequest('DELETE', `/tasks/${taskId}`);
}

export async function reorderTasks(tasks) {
  return apiRequest('POST', '/tasks/reorder', { tasks });
}

// Projects
export async function fetchProjects() {
  return apiRequest('GET', '/projects');
}

export async function createProject(project) {
  return apiRequest('POST', '/projects', project);
}

export async function updateProject(project) {
  return apiRequest('PUT', `/projects/${project.id || project.projectId}`, project);
}

export async function deleteProject(projectId) {
  return apiRequest('DELETE', `/projects/${projectId}`);
}

// Areas
export async function fetchAreas() {
  return apiRequest('GET', '/areas');
}

export async function createArea(area) {
  return apiRequest('POST', '/areas', area);
}

export async function updateArea(area) {
  return apiRequest('PUT', `/areas/${area.id || area.areaId}`, area);
}

export async function deleteArea(areaId) {
  return apiRequest('DELETE', `/areas/${areaId}`);
}

// Profile
export async function fetchProfile() {
  return apiRequest('GET', '/profile');
}

export async function trackUsage(metric) {
  return apiRequest('PATCH', '/profile/track-usage', { metric });
}

export async function deleteAccount() {
  return apiRequest('DELETE', '/profile');
}

// Subscription
export async function fetchSubscriptionStatus() {
  return apiRequest('GET', '/subscription/status');
}

// AI — Brain dump
// Uses its own request path (not apiRequest) because errors are surfaced
// inside the BrainDump modal rather than as global toasts, and the caller
// needs the status/code (429 BRAINDUMP_CAP → upgrade prompt).
export async function aiBrainDump(text) {
  const idToken = await getIdToken();
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = idToken;

  const res = await fetch(`${API_BASE}/ai/braindump`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      toast.error('Session expired. Please sign in again.');
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
    const err = new Error(data.error || data.message || `Brain dump failed (${res.status})`);
    err.status = res.status;
    err.code = data.code;
    err.upgrade = !!data.upgrade;
    throw err;
  }
  return data; // { tasks: [{ title, notes?, when?, dueDate? }], remaining }
}

// Tags
export async function fetchTags() {
  return apiRequest('GET', '/tags');
}

export async function createTag(tag) {
  return apiRequest('POST', '/tags', tag);
}

export async function deleteTag(tagId) {
  return apiRequest('DELETE', `/tags/${tagId}`);
}
